import { useContext, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router";
import { TabPositionProvider } from "./TabPositionContext.tsx";
import ActionList from "../action/ActionList.tsx";
import EditAction from "../action/Edit/EditAction.tsx";
import AuthenticationContext from "./AuthenticationContext";
import Layout from "./Layout";
import AppMainMenu from "./components/AppMainMenu";
import Company from "../company";
import ClientList from "../client/List";
import SegmentEdit from "../segment/EditSegment";
import SegmentList from "../segment/List";
import TaskList from "../task/List";
import Profile from "../profile/Profile";
import CompanyConfiguration from "../company/Configuration";
import UserList from "../user/List";
import ClientDetail from "../client/ClientDetail";
import ClientDetailConsolidated from "../client/ClientDetailConsolidated";
import ProcessLog from "../company/Configuration/ProcessLog";
import ImportLog from "../company/Configuration/ImportLog";
import ErrorBoundary from "./ErrorBoundary";
import ReportRoutes from "./ReportRoutes";
import { AdvancedFiltersProvider } from "./components/AdvancedFilters";
import DashboardPanelPreview from "../company/DashboardPanelPreview";
import SegmentClientList from "../segment/SegmentClientList";
import ImportTemplateManual from "./components/ImportTemplateManual";
import { InboxListContainer } from "../inbox/InboxListContainer";
import AgendaFull from "../agenda/AgendaFull";
import { FileLog } from "../import/FileLog";
import { FileDownloadReport } from "../file/FileDownloadReport";
import ImportFinish from "../account/ImportFinish";
import ImportDictionaries from "../company/Configuration/ImportTemplate/Components/ImportDictionaries";
import ImportTemplateEditDictionaryManual from "../company/Configuration/ImportTemplate/ImportTemplateEditDictionaryManual";
import ScheduledTaskList from "../task/components/scheduled/ScheduledTaskList";
import { WhatsappBase } from "../../components/whatsapp/WhatsappBase";
import { WindowManager } from "../../components/windows/WindowManager";
import ErrorBoundaryPreview from "./ErrorBoundaryPreview";
import Consolidated from "../consolidated/Consolidated";
import ConsolidatedClients from "../consolidated/ConsolidatedClients";
import ConsolidatedReport from "../consolidated/ConsolidatedReport";
import ConsolidatedSettings from "../consolidated/ConsolidatedSettings";
import CompanyService from "../../services/CompanyService";
import { ToastService } from "./bootstrap/Toast";
import { TranslationService } from "../../services/TranslationService";

const Application = () => {
  return (
    <div className="App">
      <Switch>
        <IsolatedRoute
          path="/:lang/company/edit"
          component={CompanyConfiguration}
        />
        <IsolatedRoute path="/:lang/edit" component={CompanyConfiguration} />
        <IsolatedRoute
          path="/:lang/configuration"
          component={CompanyConfiguration}
        />
        <IsolatedRoute path="/:lang/user" component={UserList} />
        <IsolatedRoute path="/:lang/company/user" component={UserList} />
        <IsolatedRoute path="/:lang/company/process" component={ProcessLog} />
        <IsolatedRoute path="/:lang/company/imports" component={ImportLog} />
        <IsolatedRoute
          path="/:lang/company/dashboardpanelpreview"
          component={DashboardPanelPreview}
        />
        <IsolatedRoute path="/:lang/action/editAction" component={EditAction} />
        <IsolatedRoute path="/:lang/action" component={ActionList} />
        <IsolatedRoute path="/:lang/client/details" component={ClientDetail} />
        <IsolatedRoute path="/:lang/client" component={ClientList} />
        <IsolatedRoute path="/:lang/company" component={Company} />
        <IsolatedRoute path="/:lang/dashboard" component={Company} />
        <IsolatedRoute path="/:lang/segment/edit" component={SegmentEdit} />
        <IsolatedRoute
          path="/:lang/segment/details"
          component={SegmentClientList}
        />
        <IsolatedRoute path="/:lang/segment" component={SegmentList} />
        <IsolatedRoute path="/:lang/task" component={TaskList} />
        <IsolatedRoute path="/:lang/inbox" component={InboxListContainer} />
        <IsolatedRoute path="/:lang/agenda/full" component={AgendaFull} />
        <IsolatedRoute path="/:lang/account/edit" component={Profile} />
        <IsolatedRoute path="/:lang/report" component={ReportRoutes} />
        <IsolatedRoute
          path="/:lang/importTemplateManual"
          component={ImportTemplateManual}
        />
        <IsolatedRoute path="/:lang/import/filelog" component={FileLog} />
        <IsolatedRoute
          path="/:lang/importdictionary"
          component={ImportDictionaries}
        />
        <IsolatedRoute
          path="/:lang/import/dictionarytemplate"
          component={ImportTemplateEditDictionaryManual}
        />
        <IsolatedRoute path="/:lang/processlog" component={ProcessLog} />
        <IsolatedRoute path="/:lang/import/finish" component={ImportFinish} />
        {/* TODO: ORDENAR URLS DUPLICADAS POR CONVIVENCIA DE V1 Y V2 */}
        <IsolatedRoute
          path="/:lang/files/downloadreport"
          component={FileDownloadReport}
        />
        <IsolatedRoute
          path="/:lang/backup/download"
          component={FileDownloadReport}
        />
        <IsolatedRoute
          path="/:lang/scheduledtask"
          component={ScheduledTaskList}
        />
        <IsolatedRoute
          path="/:lang/errorboundary/preview"
          component={ErrorBoundaryPreview}
        />
        <ConsolidatedRoute
          path="/:lang/consolidated/client/details"
          component={ClientDetailConsolidated}
        />
        <ConsolidatedRoute
          path="/:lang/consolidated/clients"
          component={ConsolidatedClients}
        />
        <ConsolidatedRoute
          path="/:lang/consolidated/report"
          component={ConsolidatedReport}
        />
        <ConsolidatedRoute
          path="/:lang/consolidated/settings"
          component={ConsolidatedSettings}
        />
        <ConsolidatedRoute
          path="/:lang/consolidated"
          component={Consolidated}
        />
      </Switch>
    </div>
  );
};

const IsolatedRoute = (props) => {
  return (
    <Route path={props.path}>
      <AdvancedFiltersProvider key={props.path?.toString()}>
        <WindowManager />
        <RouteBoundary component={props.component} />
      </AdvancedFiltersProvider>
    </Route>
  );
};

const ConsolidatedRoute = (props) => {
  return (
    <Route
      path={props.path}
      render={({ match }) => {
        const lang = match?.params?.lang ?? "es";
        if (!CompanyService.canViewConsolidated()) {
          return <Redirect to={`/${lang}/company`} />;
        }
        // La pantalla de configuración queda afuera de este chequeo:
        // es adonde redirigimos, así que no puede exigirse a sí misma.
        const isSettingsRoute = props.path === "/:lang/consolidated/settings";
        if (!isSettingsRoute && !CompanyService.isConsolidatedSetted()) {
          return <ConsolidatedSettingsRedirect lang={lang} />;
        }
        return (
          <AdvancedFiltersProvider key={props.path?.toString()}>
            <WindowManager />
            <RouteBoundary component={props.component} />
          </AdvancedFiltersProvider>
        );
      }}
    />
  );
};

const ConsolidatedSettingsRedirect = ({ lang }) => {
  useEffect(() => {
    ToastService.showToast(
      TranslationService.translate.MustConsolidateAtLeastOneCompany,
      undefined,
      "warning"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Redirect to={`/${lang}/consolidated/settings`} />;
};

const RouteBoundary = (props) => {
  const Component = props.component;
  const location = useLocation();
  return (
    <ErrorBoundary location={location.pathname + location.search}>
      <Component />
    </ErrorBoundary>
  );
};

const BaseSite = (props) => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const location = useLocation();
  const returnUrl = location.pathname + location.search;

  return isAuthenticated ? (
    <TabPositionProvider>
      <div className="app-shell">
        <AppMainMenu />
        <div className="app-shell__content">
          <Layout />
          <Application />
        </div>
      </div>
      <WhatsappBase />
    </TabPositionProvider>
  ) : (
    <Redirect to={`/${props.lang}/logon?returnUrl=${returnUrl}`} />
  );
};

export default BaseSite;
